import type { ContentEntry, ContentLocalization } from "@blog/content-schema";

/**
 * The article list's data shaping (TASK-802).
 *
 * Plain functions rather than logic inside the component, because "filter by
 * status and sort by title" is exactly the kind of thing that is tedious to
 * click through and trivial to test.
 */

/** One row: a localization, with the entry it belongs to. */
export interface ArticleRow {
  readonly entry: ContentEntry;
  readonly localization: ContentLocalization;
}

export type SortKey = "updated" | "published" | "title" | "status";
export type SortDirection = "asc" | "desc";

export interface ListFilters {
  /** Case-insensitive substring match against the title and slug. */
  readonly search?: string;
  readonly status?: string;
  readonly locale?: string;
}

/**
 * Join entries with their localizations, one row per localization.
 *
 * Category and tag filtering (SPEC §56 / TASK-802) is deliberately absent:
 * those live in the Markdown frontmatter and are not columns on
 * `content_localizations`, so filtering them here would mean filtering on data
 * this layer does not have. It belongs in the query, not in the browser.
 */

/** Join entries with their localizations, one row per localization. */
export function buildRows(
  entries: readonly ContentEntry[],
  localizations: readonly ContentLocalization[],
): readonly ArticleRow[] {
  const byEntry = new Map(entries.map((entry) => [entry.id, entry]));
  const rows: ArticleRow[] = [];

  for (const localization of localizations) {
    const entry = byEntry.get(localization.entryId);

    // A localization whose entry was not in this page of results is skipped
    // rather than rendered against a placeholder: it would be a row the user
    // cannot open.
    if (entry !== undefined) {
      rows.push({ entry, localization });
    }
  }

  return rows;
}

/** Apply every filter that was provided. */
export function filterRows(
  rows: readonly ArticleRow[],
  filters: ListFilters,
): readonly ArticleRow[] {
  const search = filters.search?.trim().toLowerCase() ?? "";

  return rows.filter((row) => {
    if (filters.status !== undefined && filters.status !== "" && row.localization.status !== filters.status) {
      return false;
    }

    if (filters.locale !== undefined && filters.locale !== "" && row.localization.locale !== filters.locale) {
      return false;
    }

    if (search !== "") {
      const haystack = `${row.localization.title} ${row.localization.slug}`.toLowerCase();

      if (!haystack.includes(search)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Sort rows.
 *
 * Ties fall back to the title so the order is stable — an unstable sort makes a
 * list that reshuffles on every keystroke, which reads as a bug.
 */
export function sortRows(
  rows: readonly ArticleRow[],
  key: SortKey,
  direction: SortDirection = "desc",
): readonly ArticleRow[] {
  const factor = direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const comparison = compare(a, b, key);

    if (comparison !== 0) {
      return comparison * factor;
    }

    return a.localization.title.localeCompare(b.localization.title);
  });
}

function compare(a: ArticleRow, b: ArticleRow, key: SortKey): number {
  switch (key) {
    case "title":
      return a.localization.title.localeCompare(b.localization.title);
    case "published":
      return compareInstants(a.localization.publishedAt, b.localization.publishedAt);
    case "status":
      return a.localization.status.localeCompare(b.localization.status);
    case "updated":
      return compareInstants(a.localization.updatedAt, b.localization.updatedAt);
  }
}

/** ISO-8601 instants sort lexicographically, and an absent one sorts last. */
function compareInstants(a: string | undefined, b: string | undefined): number {
  if (a === b) {
    return 0;
  }

  if (a === undefined) {
    return -1;
  }

  if (b === undefined) {
    return 1;
  }

  return a.localeCompare(b);
}

/** Count rows per status, for the filter chips. */
export function countByStatus(rows: readonly ArticleRow[]): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const row of rows) {
    counts[row.localization.status] = (counts[row.localization.status] ?? 0) + 1;
  }

  return counts;
}
